'use client'

import * as React from 'react'
import { cn } from '../../../lib/utils'
import Icon, { iconTypes } from '../Icon'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../Collapsible'
import {
  QueryField,
  SectionRelationConfig,
  SectionRelationValue,
} from '../QueryBar'
import {
  getCalendarRelationFields,
  isCalendarSessionField,
  shouldShowCalendarRelationPicker,
} from '../QueryBar/utils/nestedCalendarDefinitions'

export type ViewType = 'spreadsheet' | 'kanban' | 'calendar'

export interface LayoutOptionsPanelProps {
  view: ViewType
  onViewChange: (view: ViewType) => void
  onSortChange?: (
    sort:
      | { key: string; direction?: 'asc' | 'desc' }
      | Array<{ key: string; direction?: 'asc' | 'desc' }>,
  ) => void
  fields: (QueryField & { isSelected: boolean; showModelLabel?: boolean })[]
  sectionableFields?: QueryField[]
  sort?:
    | { key: string; direction?: 'asc' | 'desc' }
    | Array<{ key: string; direction?: 'asc' | 'desc' }>
  onFieldSelectionChange?: (
    field: QueryField,
    isSelected: boolean,
    allSelectedValues: string[],
  ) => void
  onSectionChange?: (field: QueryField | null, groupKey?: string) => void
  onSubsectionChange?: (field: QueryField | null, groupKey?: string) => void
  selectedSectionLabel?: string
  selectedSubsectionLabel?: string
  calendarStartField?: QueryField | null
  calendarEndField?: QueryField | null
  calendarRecurrenceField?: QueryField | null
  calendarSeriesIdField?: QueryField | null
  calendarOriginalStartField?: QueryField | null
  calendarField?: QueryField | null
  calendarFieldMap?: Record<string, string>
  queryFields?: QueryField[]
  onCalendarStartFieldChange?: (field: QueryField | null) => void
  onCalendarEndFieldChange?: (field: QueryField | null) => void
  onCalendarRecurrenceFieldChange?: (field: QueryField | null) => void
  onCalendarSeriesIdFieldChange?: (field: QueryField | null) => void
  onCalendarOriginalStartFieldChange?: (field: QueryField | null) => void
  onCalendarFieldChange?: (field: QueryField | null) => void
  onCalendarFieldMapChange?: (next: Record<string, string>) => void
  kanbanStatusBadgeField?: QueryField | null
  kanbanDescriptionField?: QueryField | null
  onKanbanStatusBadgeFieldChange?: (field: QueryField | null) => void
  onKanbanDescriptionFieldChange?: (field: QueryField | null) => void
  sectionRelationConfig?: SectionRelationConfig
}

interface ViewOption {
  type: ViewType
  label: string
  icon: iconTypes
}

const getIconFromFieldType = (
  dataType: QueryField['dataType'],
  hasOptions: boolean,
) => {
  if (hasOptions) {
    return 'MousePointerClick'
  }

  switch (dataType) {
    case 'STRING':
      return 'LetterText'
    case 'NUMBER':
      return 'Hash'
    case 'DATE':
      return 'Calendar'
    case 'DATE_TIME':
      return 'Calendar'
    case 'TIME':
      return 'Clock4'
    case 'BOOLEAN':
      return 'SquareCheck'
    default:
  }

  return 'Tag'
}

export const LayoutOptionsPanel: React.FC<LayoutOptionsPanelProps> = ({
  view,
  onViewChange,
  onSortChange,
  fields,
  sectionableFields,
  sort,
  onFieldSelectionChange,
  onSectionChange,
  onSubsectionChange,
  selectedSectionLabel,
  selectedSubsectionLabel,
  calendarStartField,
  calendarEndField,
  calendarField,
  onCalendarStartFieldChange,
  onCalendarEndFieldChange,
  onCalendarFieldChange,
  onCalendarFieldMapChange,
  kanbanStatusBadgeField,
  kanbanDescriptionField,
  onKanbanStatusBadgeFieldChange,
  onKanbanDescriptionFieldChange,
  sectionRelationConfig,
}) => {
  const [relationSearchQuery, setRelationSearchQuery] = React.useState('')
  const [pendingStatusBadgeField, setPendingStatusBadgeField] =
    React.useState<QueryField | null>(kanbanStatusBadgeField ?? null)
  const [pendingDescriptionField, setPendingDescriptionField] =
    React.useState<QueryField | null>(kanbanDescriptionField ?? null)
  const [pendingSelectedFieldValues, setPendingSelectedFieldValues] =
    React.useState<Set<string>>(
      () =>
        new Set(fields.filter((field) => field.isSelected).map((f) => f.value)),
    )

  React.useEffect(() => {
    setPendingStatusBadgeField(kanbanStatusBadgeField ?? null)
  }, [kanbanStatusBadgeField?.value])

  React.useEffect(() => {
    setPendingDescriptionField(kanbanDescriptionField ?? null)
  }, [kanbanDescriptionField?.value])

  React.useEffect(() => {
    setPendingSelectedFieldValues((previous) => {
      const fromProps = new Set(
        fields.filter((field) => field.isSelected).map((field) => field.value),
      )
      if (fromProps.size === 0) {
        return previous
      }
      const merged = new Set(previous)
      fromProps.forEach((value) => merged.add(value))
      return merged
    })
  }, [fields.map((field) => `${field.value}:${field.isSelected}`).join('|')])

  const displayedStatusBadgeField = pendingStatusBadgeField
  const displayedDescriptionField = pendingDescriptionField

  const isFieldSelected = (fieldValue: string) =>
    pendingSelectedFieldValues.has(fieldValue)

  const [relationSearchResults, setRelationSearchResults] = React.useState<
    SectionRelationValue[]
  >([])
  const [isRelationSearching, setIsRelationSearching] = React.useState(false)
  const relationSearchInputRef = React.useRef<HTMLInputElement | null>(null)
  const relationSectionContainerRef = React.useRef<HTMLDivElement | null>(null)

  const [openTopLevelAccordion, setOpenTopLevelAccordion] = React.useState<
    string | null
  >(null)
  const ACCORDION_TRANSITION_MS = 220
  const accordionCloseTimeoutRef = React.useRef<number | null>(null)
  const pendingAccordionToOpenRef = React.useRef<string | null>(null)

  const clearAccordionCloseTransition = React.useCallback(() => {
    if (accordionCloseTimeoutRef.current) {
      window.clearTimeout(accordionCloseTimeoutRef.current)
      accordionCloseTimeoutRef.current = null
    }
    pendingAccordionToOpenRef.current = null
  }, [])

  const closeCurrentAccordion = React.useCallback(() => {
    clearAccordionCloseTransition()
    setOpenTopLevelAccordion(null)
  }, [clearAccordionCloseTransition])

  const queueAccordionOpen = React.useCallback(
    (target: string) => {
      if (openTopLevelAccordion === target) return
      if (!openTopLevelAccordion) {
        clearAccordionCloseTransition()
        setOpenTopLevelAccordion(target)
        return
      }
      clearAccordionCloseTransition()
      pendingAccordionToOpenRef.current = target
      setOpenTopLevelAccordion(null)
      accordionCloseTimeoutRef.current = window.setTimeout(() => {
        if (pendingAccordionToOpenRef.current) {
          setOpenTopLevelAccordion(pendingAccordionToOpenRef.current)
          pendingAccordionToOpenRef.current = null
        }
        accordionCloseTimeoutRef.current = null
      }, ACCORDION_TRANSITION_MS)
    },
    [clearAccordionCloseTransition, openTopLevelAccordion],
  )

  const handleTopLevelAccordionChange = React.useCallback(
    (key: string, isOpen: boolean) => {
      if (isOpen) {
        queueAccordionOpen(key)
        return
      }
      if (openTopLevelAccordion === key) {
        closeCurrentAccordion()
      } else {
        clearAccordionCloseTransition()
      }
    },
    [
      clearAccordionCloseTransition,
      closeCurrentAccordion,
      openTopLevelAccordion,
      queueAccordionOpen,
    ],
  )

  React.useEffect(
    () => () => {
      clearAccordionCloseTransition()
    },
    [clearAccordionCloseTransition],
  )

  const currentSort = React.useMemo(
    () => (Array.isArray(sort) ? sort?.[0] : sort),
    [sort],
  )

  const sortFieldLabel = React.useMemo(() => {
    const fieldKey = currentSort?.key
    if (!fieldKey) return ''
    const exact = fields.find((field) => field.value === fieldKey)
    if (exact) return exact.label
    const leaf = fieldKey.split('.').pop() || fieldKey
    const byLeaf = fields.find((field) => {
      const value = String(field.value || '')
      return value === fieldKey || value.split('.').pop() === leaf
    })
    return byLeaf?.label ?? leaf
  }, [currentSort?.key, fields])

  const directionLabel = React.useMemo(() => {
    const dir = currentSort?.direction || 'asc'
    return dir === 'desc' ? 'DESCENDING' : 'ASCENDING'
  }, [currentSort?.direction])

  const inlineChipClassName =
    'flex-shrink-0 flex items-center gap-1 max-w-[90px] rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground -mr-1'

  const handleRelationSearch = React.useCallback(
    async (query: string) => {
      setRelationSearchQuery(query)
      if (!sectionRelationConfig) {
        setRelationSearchResults([])
        return
      }
      const trimmed = query.trim()
      if (!trimmed) {
        setRelationSearchResults([])
        return
      }
      try {
        setIsRelationSearching(true)
        const next = await sectionRelationConfig.search(trimmed)
        setRelationSearchResults(next)
      } catch {
        setRelationSearchResults([])
      } finally {
        setIsRelationSearching(false)
      }
    },
    [sectionRelationConfig],
  )

  const toggleRelationValue = React.useCallback(
    (value: SectionRelationValue) => {
      if (!sectionRelationConfig) return
      const exists = sectionRelationConfig.values.some((v) => v.id === value.id)
      const nextValues = exists
        ? sectionRelationConfig.values.filter((v) => v.id !== value.id)
        : [...sectionRelationConfig.values, value]
      sectionRelationConfig.onChange(nextValues)
    },
    [sectionRelationConfig],
  )

  React.useEffect(() => {
    if (!sectionRelationConfig) return
    if (openTopLevelAccordion !== 'section') return
    if (!relationSearchInputRef.current) return
    relationSearchInputRef.current.focus()
  }, [
    sectionRelationConfig,
    openTopLevelAccordion,
    relationSearchResults,
    isRelationSearching,
  ])

  type CollapsibleMenuItem = {
    id: string
    label: string
    icon?: iconTypes
    onClick?: () => void
    children?: CollapsibleMenuItem[]
    content?: React.ReactNode
  }

  const CollapsibleMenuNode: React.FC<{
    item: CollapsibleMenuItem
    level?: number
    openAccordions?: Map<number, string | null>
    onAccordionChange?: (level: number, id: string | null) => void
  }> = ({ item, level = 0, openAccordions, onAccordionChange }) => {
    const hasChildren = !!item.children && item.children.length > 0
    const hasContentOnly = !hasChildren && !!item.content
    const indentClass = level > 0 ? 'border-l border-accent/30' : undefined

    if (!hasChildren && !item.content) {
      return (
        <button
          type="button"
          className={cn(
            'flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70',
            indentClass,
          )}
          onClick={item.onClick}
        >
          {item.icon && (
            <Icon name={item.icon} size={12} className="flex-shrink-0" />
          )}
          <span className="truncate">{item.label}</span>
        </button>
      )
    }

    if (hasContentOnly) {
      return (
        <div
          className={cn(
            'flex flex-col gap-0.5 border border-accent/80 rounded-md bg-background p-1',
            indentClass,
          )}
        >
          <div className="flex w-full items-center justify-between px-2 py-1 text-2xs rounded-md">
            <span className="flex items-center gap-2 min-w-0">
              {item.icon && (
                <Icon name={item.icon} size={12} className="flex-shrink-0" />
              )}
              <span className="truncate">{item.label}</span>
            </span>
          </div>
          <div className="mt-1">{item.content}</div>
        </div>
      )
    }

    const isOpen = openAccordions?.get(level) === item.id

    const handleOpenChange = (newOpen: boolean) => {
      if (newOpen) {
        onAccordionChange?.(level, item.id)
      } else {
        onAccordionChange?.(level, null)
        for (let i = level + 1; i < 10; i++) {
          onAccordionChange?.(i, null)
        }
      }
    }

    return (
      <div
        className={cn(
          'flex flex-col gap-0.5',
          isOpen && 'border border-accent/80 rounded-md bg-background p-1',
        )}
      >
        <Collapsible open={isOpen} onOpenChange={handleOpenChange}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className={cn(
                'flex w-full items-center justify-between px-2 py-1 text-2xs rounded-md hover:bg-accent/70',
                indentClass,
              )}
            >
              <span className="flex items-center gap-2 min-w-0">
                {item.icon && (
                  <Icon name={item.icon} size={12} className="flex-shrink-0" />
                )}
                <span className="truncate">{item.label}</span>
              </span>
              <Icon
                name={isOpen ? 'ChevronDown' : 'ChevronRight'}
                size={10}
                className="ml-1 flex-shrink-0 text-muted-foreground"
              />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="p-1">
            <div
              className={cn(
                'mt-1 flex flex-col gap-0.5',
                'border-l border-accent/30',
              )}
            >
              {item.content}
              {item.children?.map((child) => (
                <CollapsibleMenuNode
                  key={child.id}
                  item={child}
                  level={level + 1}
                  openAccordions={openAccordions}
                  onAccordionChange={onAccordionChange}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>
    )
  }

  const CollapsibleMenuList: React.FC<{
    items: CollapsibleMenuItem[]
  }> = ({ items }) => {
    const [openAccordions, setOpenAccordions] = React.useState<
      Map<number, string | null>
    >(new Map())

    const handleAccordionChange = React.useCallback(
      (level: number, id: string | null) => {
        setOpenAccordions((prev) => {
          const next = new Map(prev)
          next.set(level, id)
          return next
        })
      },
      [],
    )

    return (
      <div className="flex flex-col gap-0.5 py-1">
        {items.map((item) => (
          <CollapsibleMenuNode
            key={item.id}
            item={item}
            openAccordions={openAccordions}
            onAccordionChange={handleAccordionChange}
          />
        ))}
      </div>
    )
  }

  const getSectionableFields = () =>
    (sectionableFields && sectionableFields.length > 0
      ? sectionableFields
      : (fields || []).map((f) => ({
          label: f.label,
          value: f.value,
          modelLabel: f.modelLabel,
          modelvalue: f.modelvalue,
          showModelLabel: f.showModelLabel,
          dataType: f.dataType,
          options: f.options,
          definitionHandle: f.definitionHandle,
        }))) as QueryField[]

  const getCalendarSessionFields = () =>
    getSectionableFields().filter(isCalendarSessionField)

  const calendarRelationFields = getCalendarRelationFields(
    getSectionableFields(),
  )

  const buildRelationSectionContent = () => {
    if (!sectionRelationConfig) return null

    const selectedIds = new Set(
      sectionRelationConfig?.values.map((v) => v.id) ?? [],
    )
    const combinedOptions: SectionRelationValue[] = [
      ...(sectionRelationConfig?.values ?? []),
      ...relationSearchResults.filter(
        (opt) =>
          !(sectionRelationConfig?.values ?? []).some((v) => v.id === opt.id),
      ),
    ]

    return (
      <div
        ref={relationSectionContainerRef}
        className="flex flex-col gap-1 p-1 w-full ml-0"
      >
        <input
          ref={relationSearchInputRef}
          className="w-full px-2 py-1 text-2xs border rounded-md outline-none focus:ring-1 focus:ring-ring"
          placeholder="Search…"
          value={relationSearchQuery}
          onChange={(e) => {
            void handleRelationSearch(e.target.value)
          }}
          onBlur={(e) => {
            const next = e.relatedTarget as HTMLElement | null
            const container = relationSectionContainerRef.current
            if (!container) return
            if (next && container.contains(next)) return
            if (openTopLevelAccordion !== 'section') return
            window.setTimeout(() => {
              relationSearchInputRef.current?.focus()
            }, 0)
          }}
        />
        <div className="mt-1 max-h-48 overflow-y-auto rounded-sm border border-accent/80 bg-background/80 w-full">
          {isRelationSearching && (
            <div className="px-2 py-1 text-2xs text-muted-foreground">
              Searching…
            </div>
          )}
          {!isRelationSearching && combinedOptions.length === 0 && (
            <div className="px-2 py-1 text-2xs text-muted-foreground">
              No results
            </div>
          )}
          {!isRelationSearching &&
            combinedOptions.map((opt) => {
              const checked = selectedIds.has(opt.id)
              return (
                <button
                  key={opt.id}
                  type="button"
                  className={cn(
                    'flex w-full items-center justify-between px-2 py-1 text-left text-2xs hover:bg-accent/70',
                  )}
                  onClick={() => toggleRelationValue(opt)}
                >
                  <span className="truncate">{opt.label}</span>
                  {checked && (
                    <Icon
                      name="Check"
                      size={12}
                      className="ml-2 flex-shrink-0"
                    />
                  )}
                </button>
              )
            })}
        </div>
        {sectionRelationConfig?.values.length ? (
          <button
            type="button"
            className="mt-1 self-start px-2 py-0.5 text-[10px] rounded-md bg-muted text-muted-foreground hover:bg-muted/80"
            onClick={() => sectionRelationConfig.onChange([])}
          >
            Clear selection
          </button>
        ) : null}
      </div>
    )
  }

  const buildSectionMenuItems = (): CollapsibleMenuItem[] => {
    const items: CollapsibleMenuItem[] = []

    for (const f of getSectionableFields()) {
      const icon = getIconFromFieldType(f.dataType, !!f.options)
      const isRelationField = f.dataType === 'RELATION'
      const isCurrentRelationSection =
        isRelationField &&
        sectionRelationConfig &&
        sectionRelationConfig.fieldValue === f.value

      if (isCurrentRelationSection && isRelationField) {
        items.push({
          id: `section-relation-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          content: buildRelationSectionContent(),
        })
        continue
      }

      if (f.dataType === 'DATE') {
        items.push({
          id: `section-date-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `section-date-${f.value as string}-day_of_week`,
              label: 'Day of week',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'day_of_week'),
            },
            {
              id: `section-date-${f.value as string}-month_of_year`,
              label: 'Month of year',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'month_of_year'),
            },
          ],
        })
        continue
      }

      if (f.dataType === 'TIME') {
        items.push({
          id: `section-time-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `section-time-${f.value as string}-hour_of_day`,
              label: 'Hour of day',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'hour_of_day'),
            },
          ],
        })
        continue
      }

      if (f.dataType === 'DATE_TIME') {
        items.push({
          id: `section-datetime-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `section-datetime-${f.value as string}-day_of_week`,
              label: 'Day of week',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'day_of_week'),
            },
            {
              id: `section-datetime-${f.value as string}-month_of_year`,
              label: 'Month of year',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'month_of_year'),
            },
            {
              id: `section-datetime-${f.value as string}-hour_of_day`,
              label: 'Hour of day',
              icon: icon as iconTypes,
              onClick: () => onSectionChange?.(f, 'hour_of_day'),
            },
          ],
        })
        continue
      }

      items.push({
        id: `section-${f.value as string}`,
        label: f.label,
        icon: icon as iconTypes,
        onClick: () => onSectionChange?.(f),
      })
    }

    return items
  }

  const buildSubsectionMenuItems = (): CollapsibleMenuItem[] => {
    const items: CollapsibleMenuItem[] = []

    for (const f of getSectionableFields()) {
      const icon = getIconFromFieldType(f.dataType, !!f.options)

      if (f.dataType === 'DATE') {
        items.push({
          id: `subsection-date-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `subsection-date-${f.value as string}-day_of_week`,
              label: 'Day of week',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'day_of_week'),
            },
            {
              id: `subsection-date-${f.value as string}-month_of_year`,
              label: 'Month of year',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'month_of_year'),
            },
          ],
        })
        continue
      }

      if (f.dataType === 'TIME') {
        items.push({
          id: `subsection-time-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `subsection-time-${f.value as string}-hour_of_day`,
              label: 'Hour of day',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'hour_of_day'),
            },
          ],
        })
        continue
      }

      if (f.dataType === 'DATE_TIME') {
        items.push({
          id: `subsection-datetime-${f.value as string}`,
          label: f.label,
          icon: icon as iconTypes,
          children: [
            {
              id: `subsection-datetime-${f.value as string}-day_of_week`,
              label: 'Day of week',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'day_of_week'),
            },
            {
              id: `subsection-datetime-${f.value as string}-month_of_year`,
              label: 'Month of year',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'month_of_year'),
            },
            {
              id: `subsection-datetime-${f.value as string}-hour_of_day`,
              label: 'Hour of day',
              icon: icon as iconTypes,
              onClick: () => onSubsectionChange?.(f, 'hour_of_day'),
            },
          ],
        })
        continue
      }

      items.push({
        id: `subsection-${f.value as string}`,
        label: f.label,
        icon: icon as iconTypes,
        onClick: () => onSubsectionChange?.(f),
      })
    }

    return items
  }

  const views: ViewOption[] = [
    {
      type: 'spreadsheet',
      label: 'Spreadsheet',
      icon: 'TableProperties',
    },
    {
      type: 'kanban',
      label: 'Kanban',
      icon: 'SquareKanban',
    },
    {
      type: 'calendar',
      label: 'Calendar',
      icon: 'Calendar',
    },
  ]

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="grid grid-cols-3 border-1 rounded-md bg-accent/50 w-full px-1">
        {views.map((viewOption) => (
          <button
            key={viewOption.type}
            className={cn(
              'flex flex-col items-center gap-2 px-2 p-2 text-2xs hover:bg-accent/70 rounded-md w-full',
              {
                'bg-accent': view === viewOption.type,
              },
            )}
            onClick={() => {
              onViewChange(viewOption.type)
            }}
          >
            <div className="flex items-center gap-2">
              <Icon name={viewOption.icon} size={16} />
            </div>
            {viewOption.label}
          </button>
        ))}
      </div>

      {view === 'kanban' && (
        <div className="w-full flex flex-col border-t mt-2 pt-2">
          <div
            className={cn(
              'w-full rounded-md border border-transparent transition-all',
              openTopLevelAccordion === 'kanban-status-badge' &&
                'border-accent/80 bg-background',
            )}
          >
            <Collapsible
              className="w-full"
              open={openTopLevelAccordion === 'kanban-status-badge'}
              onOpenChange={(isOpen) =>
                handleTopLevelAccordionChange('kanban-status-badge', isOpen)
              }
            >
              <CollapsibleTrigger asChild>
                <div
                  className={cn(
                    'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon name="Badge" size={12} />
                    <span className="truncate">Status badge</span>
                  </span>
                  {displayedStatusBadgeField && (
                    <button
                      type="button"
                      aria-label="Clear status badge selection"
                      className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                      onClick={(event) => {
                        event.stopPropagation()
                        setPendingStatusBadgeField(null)
                        onKanbanStatusBadgeFieldChange?.(null)
                      }}
                    >
                      <span className="truncate">
                        {displayedStatusBadgeField.label}
                      </span>
                      <Icon name="X" size={8} />
                    </button>
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="p-1">
                <div className="mt-1 flex flex-col gap-0.5">
                  {fields.map((f) => {
                    const isSelected =
                      displayedStatusBadgeField?.value === f.value
                    return (
                      <button
                        key={f.value}
                        type="button"
                        className={cn(
                          'flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70',
                          isSelected && 'bg-accent/70',
                        )}
                        onClick={() => {
                          setPendingStatusBadgeField(f)
                          onKanbanStatusBadgeFieldChange?.(f)
                        }}
                      >
                        <Icon
                          name={
                            getIconFromFieldType(
                              f.dataType,
                              !!f.options,
                            ) as iconTypes
                          }
                          size={12}
                        />
                        <span className="truncate flex-1 text-left">
                          {f.label}
                        </span>
                        {isSelected ? (
                          <Icon name="Check" size={12} className="shrink-0" />
                        ) : null}
                      </button>
                    )
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>

          <div
            className={cn(
              'w-full rounded-md border border-transparent transition-all',
              openTopLevelAccordion === 'kanban-description' &&
                'border-accent/80 bg-background',
            )}
          >
            <Collapsible
              className="w-full"
              open={openTopLevelAccordion === 'kanban-description'}
              onOpenChange={(isOpen) =>
                handleTopLevelAccordionChange('kanban-description', isOpen)
              }
            >
              <CollapsibleTrigger asChild>
                <div
                  className={cn(
                    'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                  )}
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <Icon name="AlignLeft" size={12} />
                    <span className="truncate">Description</span>
                  </span>
                  {displayedDescriptionField && (
                    <button
                      type="button"
                      aria-label="Clear description selection"
                      className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                      onClick={(event) => {
                        event.stopPropagation()
                        setPendingDescriptionField(null)
                        onKanbanDescriptionFieldChange?.(null)
                      }}
                    >
                      <span className="truncate">
                        {displayedDescriptionField.label}
                      </span>
                      <Icon name="X" size={8} />
                    </button>
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="p-1">
                <div className="mt-1 flex flex-col gap-0.5">
                  {fields
                    .filter((f) => f.dataType === 'STRING')
                    .map((f) => {
                      const isSelected =
                        displayedDescriptionField?.value === f.value
                      return (
                        <button
                          key={f.value}
                          type="button"
                          className={cn(
                            'flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70',
                            isSelected && 'bg-accent/70',
                          )}
                          onClick={() => {
                            setPendingDescriptionField(f)
                            onKanbanDescriptionFieldChange?.(f)
                          }}
                        >
                          <Icon
                            name={
                              getIconFromFieldType(
                                f.dataType,
                                !!f.options,
                              ) as iconTypes
                            }
                            size={12}
                          />
                          <span className="truncate flex-1 text-left">
                            {f.label}
                          </span>
                          {isSelected ? (
                            <Icon name="Check" size={12} className="shrink-0" />
                          ) : null}
                        </button>
                      )
                    })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          </div>
        </div>
      )}

      <div className="flex flex-row flex-wrap w-full border-t px-1 pt-2 gap-0.5">
        {fields.map((field) => (
          <button
            key={field.value}
            onClick={() => {
              const nextSelected = !isFieldSelected(field.value)
              const nextValues = new Set(pendingSelectedFieldValues)
              if (nextSelected) {
                nextValues.add(field.value)
              } else {
                nextValues.delete(field.value)
              }
              const allSelectedValues = Array.from(nextValues)
              setPendingSelectedFieldValues(nextValues)
              onFieldSelectionChange?.(field, nextSelected, allSelectedValues)
            }}
            className={cn(
              'flex items-center gap-1 px-2 py-1 text-2xs rounded-md w-fit transition-colors',
              isFieldSelected(field.value)
                ? 'bg-muted-foreground/10 text-muted-foreground hover:bg-foreground/20 hover:text-foreground'
                : 'hover:bg-accent/70',
            )}
          >
            <Icon
              name={getIconFromFieldType(field.dataType, !!field.options)}
              size={12}
            />
            <span className="flex items-center gap-1">
              {field.showModelLabel && (
                <>
                  <span className="text-2xs text-muted-foreground/70">
                    {field.modelLabel}
                  </span>
                  <span className="text-2xs text-muted-foreground/50">•</span>
                </>
              )}
              <span>{field.label}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="flex flex-col w-full border-t rounded-b-md mx-1 py-2">
        {view !== 'calendar' && (
          <div className="flex flex-col w-full">
            <div
              className={cn(
                'w-full rounded-md border border-transparent transition-all',
                openTopLevelAccordion === 'sort' &&
                  'border-accent/80 bg-background',
              )}
            >
              <Collapsible
                className="w-full"
                open={openTopLevelAccordion === 'sort'}
                onOpenChange={(isOpen) =>
                  handleTopLevelAccordionChange('sort', isOpen)
                }
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon name="ArrowDownAZ" size={12} />
                      <span className="truncate">Sort by</span>
                    </div>
                    {sortFieldLabel && (
                      <div className={inlineChipClassName}>
                        <span className="truncate">{sortFieldLabel}</span>
                      </div>
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="p-1">
                  <div className="mt-1 flex flex-col gap-0.5">
                    {fields.map((field) => (
                      <button
                        key={field.value}
                        type="button"
                        className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                        onClick={() => {
                          if (onSortChange) {
                            const s = Array.isArray(sort) ? sort?.[0] : sort
                            onSortChange({
                              key: field.value,
                              direction: s?.direction || 'asc',
                            })
                          }
                        }}
                      >
                        <Icon
                          name={
                            getIconFromFieldType(
                              field.dataType,
                              !!field.options,
                            ) as iconTypes
                          }
                          size={12}
                          className="flex-shrink-0"
                        />
                        <span className="truncate">{field.label}</span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <div
              className={cn(
                'w-full rounded-md border border-transparent transition-all',
                openTopLevelAccordion === 'direction' &&
                  'border-accent/80 bg-background p-1',
              )}
            >
              <Collapsible
                className="w-full"
                open={openTopLevelAccordion === 'direction'}
                onOpenChange={(isOpen) =>
                  handleTopLevelAccordionChange('direction', isOpen)
                }
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon name="ArrowUpDown" size={12} />
                      <span>Direction</span>
                    </div>
                    {directionLabel && (
                      <div className={inlineChipClassName}>
                        <span className="truncate">{directionLabel}</span>
                      </div>
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent className="p-1">
                  <div className="mt-1 flex flex-col gap-0.5">
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                      onClick={() => {
                        if (onSortChange) {
                          const s = Array.isArray(sort) ? sort?.[0] : sort
                          if (s?.key) {
                            onSortChange({ key: s.key, direction: 'asc' })
                          }
                        }
                      }}
                    >
                      <Icon name="ArrowDownAZ" size={12} />
                      <span>Ascending</span>
                    </button>
                    <button
                      type="button"
                      className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                      onClick={() => {
                        if (onSortChange) {
                          const s = Array.isArray(sort) ? sort?.[0] : sort
                          if (s?.key) {
                            onSortChange({ key: s.key, direction: 'desc' })
                          }
                        }
                      }}
                    >
                      <Icon name="ArrowUpZA" size={12} />
                      <span>Descending</span>
                    </button>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>
          </div>
        )}

        {(((view === 'kanban' || view === 'calendar') && onSectionChange) ||
          (view === 'kanban' && onSubsectionChange)) && (
          <div className="w-full flex flex-col">
            {(view === 'kanban' || view === 'calendar') && onSectionChange && (
              <div
                className={cn(
                  'w-full rounded-md border border-transparent transition-all',
                  openTopLevelAccordion === 'section' &&
                    'border-accent/80 bg-background',
                )}
              >
                <div className="relative">
                  <Collapsible
                    className="w-full"
                    open={openTopLevelAccordion === 'section'}
                    onOpenChange={(isOpen) =>
                      handleTopLevelAccordionChange('section', isOpen)
                    }
                  >
                    <CollapsibleTrigger asChild>
                      <div
                        className={cn(
                          'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                        )}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Icon name="BetweenVerticalStart" size={12} />
                          <span className="truncate">Section by</span>
                        </span>
                        {selectedSectionLabel ? (
                          <button
                            type="button"
                            aria-label="Clear section selection"
                            className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                            onClick={(event) => {
                              event.stopPropagation()
                              onSectionChange?.(null)
                            }}
                          >
                            <span className="truncate">
                              {selectedSectionLabel}
                            </span>
                            <Icon name="X" size={8} />
                          </button>
                        ) : null}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-1">
                      <CollapsibleMenuList items={buildSectionMenuItems()} />
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            )}

            {view === 'kanban' && onSubsectionChange && (
              <div
                className={cn(
                  'w-full rounded-md border border-transparent transition-all',
                  openTopLevelAccordion === 'subsection' &&
                    'border-accent/80 bg-background',
                )}
              >
                <div className="relative">
                  <Collapsible
                    className="w-full"
                    open={openTopLevelAccordion === 'subsection'}
                    onOpenChange={(isOpen) =>
                      handleTopLevelAccordionChange('subsection', isOpen)
                    }
                  >
                    <CollapsibleTrigger asChild>
                      <div
                        className={cn(
                          'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                        )}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <Icon name="LayoutTemplate" size={12} />
                          <span className="truncate">Subsection by</span>
                        </span>
                        {selectedSubsectionLabel ? (
                          <button
                            type="button"
                            aria-label="Clear subsection selection"
                            className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                            onClick={(event) => {
                              event.stopPropagation()
                              onSubsectionChange?.(null)
                            }}
                          >
                            <span className="truncate">
                              {selectedSubsectionLabel}
                            </span>
                            <Icon name="X" size={8} />
                          </button>
                        ) : null}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="p-1">
                      <CollapsibleMenuList items={buildSubsectionMenuItems()} />
                    </CollapsibleContent>
                  </Collapsible>
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'calendar' && (
          <div className="w-full flex flex-col border-t mt-2 pt-2">
            <div
              className={cn(
                'w-full rounded-md border border-transparent transition-all',
                openTopLevelAccordion === 'calendar-start' &&
                  'border-accent/80 bg-background',
              )}
            >
              <Collapsible
                className="w-full"
                open={openTopLevelAccordion === 'calendar-start'}
                onOpenChange={(isOpen) =>
                  handleTopLevelAccordionChange('calendar-start', isOpen)
                }
              >
                <CollapsibleTrigger asChild>
                  <div
                    className={cn(
                      'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                    )}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Icon name="Calendar" size={12} />
                      <span className="truncate">Session start</span>
                    </span>
                    {calendarStartField && (
                      <button
                        type="button"
                        aria-label="Clear session start selection"
                        className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                        onClick={(event) => {
                          event.stopPropagation()
                          onCalendarStartFieldChange?.(null)
                        }}
                      >
                        <span className="truncate">
                          {calendarStartField.label}
                        </span>
                        <Icon name="X" size={8} />
                      </button>
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="p-1">
                  <div className="mt-1 flex flex-col gap-0.5">
                    {getCalendarSessionFields().map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                        onClick={() => onCalendarStartFieldChange?.(f)}
                      >
                        <Icon
                          name={
                            getIconFromFieldType(
                              f.dataType,
                              !!f.options,
                            ) as iconTypes
                          }
                          size={12}
                        />
                        <span className="truncate">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            <div
              className={cn(
                'w-full rounded-md border border-transparent transition-all',
                openTopLevelAccordion === 'calendar-end' &&
                  'border-accent/80 bg-background',
              )}
            >
              <Collapsible
                className="w-full"
                open={openTopLevelAccordion === 'calendar-end'}
                onOpenChange={(isOpen) =>
                  handleTopLevelAccordionChange('calendar-end', isOpen)
                }
              >
                <CollapsibleTrigger asChild>
                  <div
                    className={cn(
                      'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                    )}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Icon name="Clock4" size={12} />
                      <span className="truncate">Session end</span>
                    </span>
                    {calendarEndField && (
                      <button
                        type="button"
                        aria-label="Clear session end selection"
                        className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                        onClick={(event) => {
                          event.stopPropagation()
                          onCalendarEndFieldChange?.(null)
                        }}
                      >
                        <span className="truncate">
                          {calendarEndField.label}
                        </span>
                        <Icon name="X" size={8} />
                      </button>
                    )}
                  </div>
                </CollapsibleTrigger>
                <CollapsibleContent className="p-1">
                  <div className="mt-1 flex flex-col gap-0.5">
                    {getCalendarSessionFields().map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                        onClick={() => onCalendarEndFieldChange?.(f)}
                      >
                        <Icon
                          name={
                            getIconFromFieldType(
                              f.dataType,
                              !!f.options,
                            ) as iconTypes
                          }
                          size={12}
                        />
                        <span className="truncate">{f.label}</span>
                      </button>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </div>

            {(() => {
              const calendarPickers: Array<{
                key: string
                label: string
                icon: iconTypes
                selected: QueryField | null | undefined
                onChange?: (field: QueryField | null) => void
                fields: QueryField[]
              }> = shouldShowCalendarRelationPicker(calendarRelationFields)
                ? [
                    {
                      key: 'calendar-relation',
                      label: 'Calendar',
                      icon: 'Calendar',
                      selected: calendarField,
                      onChange: (field) => {
                        onCalendarFieldChange?.(field)
                        if (!field) onCalendarFieldMapChange?.({})
                      },
                      fields: calendarRelationFields,
                    },
                  ]
                : []
              return calendarPickers
            })().map((picker) => (
              <div
                key={picker.key}
                className={cn(
                  'w-full rounded-md border border-transparent transition-all',
                  openTopLevelAccordion === picker.key &&
                    'border-accent/80 bg-background',
                )}
              >
                <Collapsible
                  className="w-full"
                  open={openTopLevelAccordion === picker.key}
                  onOpenChange={(isOpen) =>
                    handleTopLevelAccordionChange(picker.key, isOpen)
                  }
                >
                  <CollapsibleTrigger asChild>
                    <div
                      className={cn(
                        'relative h-7 px-3 inline-flex items-center gap-2 rounded-md text-2xs bg-background hover:bg-accent w-full justify-between',
                      )}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <Icon name={picker.icon} size={12} />
                        <span className="truncate">{picker.label}</span>
                      </span>
                      {picker.selected && (
                        <button
                          type="button"
                          aria-label={`Clear ${picker.label} selection`}
                          className="absolute right-2 top-1/2 flex items-center gap-1 rounded-full border border-muted/40 bg-muted/70 px-2 py-0.5 text-[7px] uppercase font-medium text-muted-foreground hover:bg-foreground hover:text-background focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring focus-visible:outline-offset-2 -translate-y-1/2"
                          onClick={(event) => {
                            event.stopPropagation()
                            picker.onChange?.(null)
                          }}
                        >
                          <span className="truncate">
                            {picker.selected.label}
                          </span>
                          <Icon name="X" size={8} />
                        </button>
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="p-1">
                    <div className="mt-1 flex flex-col gap-0.5">
                      {picker.fields.length === 0 ? (
                        <span className="px-2 py-1 text-2xs text-muted-foreground">
                          No {picker.label.toLowerCase()} fields
                        </span>
                      ) : (
                        picker.fields.map((f) => (
                          <button
                            key={f.value}
                            type="button"
                            className="flex w-full items-center gap-2 px-2 py-1 text-2xs rounded-md hover:bg-accent/70"
                            onClick={() => picker.onChange?.(f)}
                          >
                            <Icon
                              name={
                                getIconFromFieldType(
                                  f.dataType,
                                  !!f.options,
                                ) as iconTypes
                              }
                              size={12}
                            />
                            <span className="truncate">{f.label}</span>
                          </button>
                        ))
                      )}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
